import { useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  FlowActions,
  FlowEdge,
  FlowNode,
  FlowState,
  PersonNodeData,
  ScopeNodeData,
} from "@/app/components/flow/types";
import { type TeamMember, useDagStore } from "./useDagStore";

type FlowStore = FlowState & FlowActions;

let draftNodeCounter = 0;

export const useFlowStore = create<FlowStore>()(
  persist(
    (set, get) => ({
      // State
      nodes: [],
      edges: [],
      selectedNodeId: null,
      sidebarOpen: false,

      // Actions
      setNodes: (nodes: FlowNode[]) => set({ nodes }),
      setEdges: (edges: FlowEdge[]) => set({ edges }),

      addNode: (node: FlowNode) => {
        const { nodes } = get();
        set({ nodes: [...nodes, node] });
      },

      removeNode: (nodeId: string) => {
        const { nodes, edges } = get();
        set({
          nodes: nodes.filter((n) => n.id !== nodeId),
          edges: edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
        });
      },

      selectNode: (nodeId: string | null) => {
        set({ selectedNodeId: nodeId, sidebarOpen: nodeId !== null });
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open, selectedNodeId: open ? get().selectedNodeId : null });
      },

      addDraftPersonNode: (position: { x: number; y: number }) => {
        const id = `draft-person-${++draftNodeCounter}`;
        const draftPersonNode: FlowNode = {
          id,
          type: "person",
          position,
          data: {
            teamMember: {
              id,
              name: `Draft Person ${draftNodeCounter}`,
              pageLink: null,
              workWithMeUrl: null,
              team: "Draft Team",
              status: ["Active"],
              role: "Member",
              pointPerson: [],
              pointPersonFor: [],
              pointPersonForIds: [],
              aboutMe: null,
              externalAccounts: [],
              notionUsers: [],
              notionUserIds: [],
              viewCount: 0,
              context: null,
              slackUserId: null,
              createdTime: new Date(),
              createdById: "",
              lastEditedTime: new Date(),
              lastEditedById: "",
            } as TeamMember,
            isDraft: true,
          } as PersonNodeData,
        };
        get().addNode(draftPersonNode);
      },

      addDraftScopeNode: (position: { x: number; y: number }, name: string) => {
        const id = `draft-scope-${++draftNodeCounter}`;
        const draftScopeNode: FlowNode = {
          id,
          type: "scope",
          position,
          data: {
            scope: {
              id,
              scope: name,
              status: "Draft",
              priority: null,
              content: null,
              assigneeIds: [],
              comfyProjectIds: [],
              comfyTaskIds: [],
              parentItemIds: [],
              subItemIds: [],
              createdTime: new Date(),
              createdById: "",
              lastEditedTime: new Date(),
              lastEditedById: "",
            },
            isDraft: true,
            draftName: name,
          } as ScopeNodeData,
        };
        get().addNode(draftScopeNode);
      },

      connectNodes: (sourceId: string, targetId: string) => {
        const { edges } = get();
        const edgeId = `${sourceId}-${targetId}`;

        // Check if edge already exists
        if (edges.some((e) => e.id === edgeId)) return;

        const newEdge: FlowEdge = {
          id: edgeId,
          source: sourceId,
          target: targetId,
          type: "default",
          animated: true,
        };

        set({ edges: [...edges, newEdge] });
      },

      disconnectNodes: (edgeId: string) => {
        const { edges } = get();
        set({ edges: edges.filter((e) => e.id !== edgeId) });
      },

      resetFlowStore: () => {
        set({
          nodes: [],
          edges: [],
          selectedNodeId: null,
          sidebarOpen: false,
        });
        // Reset the draft counter
        draftNodeCounter = 0;
      },
    }),
    {
      name: "flow-store",
      partialize: (state) => ({
        // Only persist essential data to avoid localStorage quota
        selectedNodeId: state.selectedNodeId,
        sidebarOpen: state.sidebarOpen,
        // Don't persist nodes and edges - they contain large team member objects
        // These will be reconstructed when initializeFlow is called
      }),
    },
  ),
);

// Hook to initialize flow with real data
export const useInitializeFlow = () => {
  const { teamMembers, scopes, inScope, excludedTeams } = useDagStore();

  return useMemo(() => {
    const filteredTeamMembers = teamMembers.filter((member) => !excludedTeams.has(member.team));

    // Create a set of valid notion user IDs from filtered team members
    const validNotionUserIds = new Set<string>();
    filteredTeamMembers.forEach((member) => {
      member.notionUserIds.forEach((id) => validNotionUserIds.add(id));
    });

    // Filter scopes: must be in scope AND have at least one valid owner
    const filteredScopes = scopes.filter((scope) => {
      // Must be in scope
      if (!inScope.scopes.has(scope.id)) return false;

      // Must have assignees
      if (!scope.assigneeIds || scope.assigneeIds.length === 0) {
        return false;
      }

      // Must have at least one valid assignee ID that maps to a current team member
      const hasValidOwner = scope.assigneeIds.some((assigneeId) =>
        validNotionUserIds.has(assigneeId),
      );

      return hasValidOwner;
    });

    // Create simple initial positions for nodes
    const personNodes: FlowNode[] = [];
    const scopeNodes: FlowNode[] = [];

    // Create person nodes with simple initial positions
    filteredTeamMembers.forEach((member, index) => {
      personNodes.push({
        id: member.id,
        type: "person" as const,
        position: { x: index * 50, y: 0 }, // Simple linear initial position
        data: { teamMember: member } as PersonNodeData,
      });
    });

    // Create scope nodes with simple initial positions
    filteredScopes.forEach((scope, index) => {
      scopeNodes.push({
        id: scope.id,
        type: "scope" as const,
        position: { x: index * 50, y: 200 }, // Simple linear initial position below persons
        data: { scope } as ScopeNodeData,
      });
    });

    // Create edges based on scope assignments
    const edges: FlowEdge[] = [];

    filteredScopes.forEach((scope) => {
      scope.assigneeIds.forEach((assigneeId) => {
        const teamMember = filteredTeamMembers.find((tm) => tm.notionUserIds.includes(assigneeId));
        if (teamMember) {
          edges.push({
            id: `${teamMember.id}-${scope.id}`,
            source: teamMember.id,
            target: scope.id,
            type: "default",
            animated: true,
          });
        }
      });
    });

    // Combine all nodes
    const allNodes = [...personNodes, ...scopeNodes];
    return { nodes: allNodes, edges };
  }, [teamMembers, scopes, inScope, excludedTeams]);
};
